import { loggers } from '../utils/logger';

export interface EnvironmentConfig {
  API_BASE_URL: string;
  GRAPHQL_API_URL: string;
  GRAPHQL_API_KEY: string;
  COGNITO_USER_POOL_ID: string;
  COGNITO_USER_POOL_CLIENT_ID: string;
  AWS_REGION: string;
  ENVIRONMENT: string;
}

// Extend Window interface for debug flags
declare global {
  interface Window {
    __ENV_DEBUG_LOGGED?: boolean;
    __COGNITO_DEBUG_LOGGED?: boolean;
  }
}

const getEnvironment = (): string => {
  // Debug: Log environment variables only once
  if (!window.__ENV_DEBUG_LOGGED) {
    loggers.build.log('Environment Variables:', {
      VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
      MODE: import.meta.env.MODE,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD
    });
    window.__ENV_DEBUG_LOGGED = true;
  }
  
  // In Vite, use import.meta.env instead of process.env
  if (typeof window !== "undefined") {
    return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development";
  }
  // For SSR/build time, check both Vite and Node environments
  return import.meta.env.VITE_APP_ENV || import.meta.env.MODE || "development";
};

const environments: Record<string, EnvironmentConfig> = {
  development: {
    API_BASE_URL: import.meta.env.VITE_API_URL || "http://api-local-dev.stagenote.ai:3002",
    GRAPHQL_API_URL: import.meta.env.VITE_GRAPHQL_API_URL || "http://localhost:3002/graphql",
    GRAPHQL_API_KEY: import.meta.env.VITE_GRAPHQL_API_KEY || "",
    COGNITO_USER_POOL_ID: "local-tutor-pool",
    COGNITO_USER_POOL_CLIENT_ID: "local-tutor-client",
    AWS_REGION: "us-east-1",
    ENVIRONMENT: "development"
  },  dev: {
    API_BASE_URL: import.meta.env.VITE_API_URL || "https://1um5wzxb5e.execute-api.ap-southeast-2.amazonaws.com/dev",
    GRAPHQL_API_URL: import.meta.env.VITE_GRAPHQL_API_URL || "https://gb4xr54k3zhbbni6cin7oy6bnu.appsync-api.ap-southeast-2.amazonaws.com/graphql",
    GRAPHQL_API_KEY: import.meta.env.VITE_GRAPHQL_API_KEY || "da2-wae5ypiy5rdjlcoqlmdo6q3fuu",
    COGNITO_USER_POOL_ID: import.meta.env.VITE_DEV_COGNITO_USER_POOL_ID || "ap-southeast-2_uhEm7LA36",
    COGNITO_USER_POOL_CLIENT_ID: import.meta.env.VITE_DEV_COGNITO_USER_POOL_CLIENT_ID || "1lgn41amkdugiq1q6pkkqvl6t4",
    AWS_REGION: "ap-southeast-2",
    ENVIRONMENT: "dev"
  },  staging: {
    API_BASE_URL: import.meta.env.VITE_API_URL || "https://yot278456b.execute-api.ap-southeast-2.amazonaws.com/staging",
    GRAPHQL_API_URL: import.meta.env.VITE_GRAPHQL_API_URL || "https://gdoamlk5krexvcar3ahfnqqyxa.appsync-api.ap-southeast-2.amazonaws.com/graphql",
    GRAPHQL_API_KEY: import.meta.env.VITE_GRAPHQL_API_KEY || "da2-5zb3g4gpzjcyrhewsqszcqko2e",
    COGNITO_USER_POOL_ID: import.meta.env.VITE_STAGING_COGNITO_USER_POOL_ID || "ap-southeast-2_srhiBNGi3",
    COGNITO_USER_POOL_CLIENT_ID: import.meta.env.VITE_STAGING_COGNITO_USER_POOL_CLIENT_ID || "4p7dvagbad5dviavb8m5o5r5g0",
    AWS_REGION: "ap-southeast-2",
    ENVIRONMENT: "staging"
  },
  production: {
    API_BASE_URL: import.meta.env.VITE_API_URL || "https://api.stagenote.ai", // TODO: Replace with actual production API URL
    GRAPHQL_API_URL: import.meta.env.VITE_GRAPHQL_API_URL || "https://PROD_APPSYNC_ID.appsync-api.ap-southeast-2.amazonaws.com/graphql",
    GRAPHQL_API_KEY: import.meta.env.VITE_GRAPHQL_API_KEY || import.meta.env.VITE_PROD_GRAPHQL_API_KEY || "",
    COGNITO_USER_POOL_ID: import.meta.env.VITE_PROD_COGNITO_USER_POOL_ID || "PROD_POOL_ID_PLACEHOLDER",
    COGNITO_USER_POOL_CLIENT_ID: import.meta.env.VITE_PROD_COGNITO_USER_POOL_CLIENT_ID || "PROD_CLIENT_ID_PLACEHOLDER",
    AWS_REGION: "ap-southeast-2",
    ENVIRONMENT: "production"
  },  prod: {
    API_BASE_URL: import.meta.env.VITE_API_URL || "https://p1rrlgv3ua.execute-api.ap-southeast-2.amazonaws.com/prod",
    GRAPHQL_API_URL: import.meta.env.VITE_GRAPHQL_API_URL || "https://ov776fosqzez7prpyij7ch3nau.appsync-api.ap-southeast-2.amazonaws.com/graphql",
    GRAPHQL_API_KEY: import.meta.env.VITE_GRAPHQL_API_KEY || "da2-tbjjjzjjg5bkxkeixqr24atxca",
    COGNITO_USER_POOL_ID: import.meta.env.VITE_PROD_COGNITO_USER_POOL_ID || "ap-southeast-2_38SLbtQIC",
    COGNITO_USER_POOL_CLIENT_ID: import.meta.env.VITE_PROD_COGNITO_USER_POOL_CLIENT_ID || "3i3mp9nnim5ve4t4et2la1180p",
    AWS_REGION: "ap-southeast-2",
    ENVIRONMENT: "prod"
  }
};

export const config: EnvironmentConfig = environments[getEnvironment()];

// Helper function to get the current environment
export const getCurrentEnvironment = (): string => getEnvironment();

// Helper function to check if we're in development mode
export const isDevelopment = (): boolean => getEnvironment() === "development";

// Helper function to check if we're in production mode
export const isProduction = (): boolean => getEnvironment() === "production" || getEnvironment() === "prod";

// API endpoints for the tutor app
export const API_ENDPOINTS = {
  STUDENTS: `${config.API_BASE_URL}/students`,
  LESSONS: `${config.API_BASE_URL}/lessons`,
  INVOICES: `${config.API_BASE_URL}/invoices`,
  LOCATIONS: `${config.API_BASE_URL}/locations`,
  LOCATION_AVAILABILITY: `${config.API_BASE_URL}/location-availability`,
  PREDEFINED_CALENDARS: `${config.API_BASE_URL}/predefined-calendars`,
  CALENDAR_EVENTS: `${config.API_BASE_URL}/calendar/events`,
} as const; 